import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { useOnResize } from "@client/utils/useOnResize";
import { Funder, Recommender } from "@engine/round/aggregate";
import { scaleLinear } from "d3-scale";
import { sumBy } from "lodash";
import { useRef, useState } from "react";
import { FrontendEvaluation } from "../useEvaluatorEvaluationsCache";
import {
  AllocationChartItem,
  AllocationChartItemOther,
  AllocationChartItemProps,
} from "./AllocationChartItem";

const numTopAllocations = 24;
const minimumEntryHeight = 15;
const pixOfSmolsToShowBelowOther = minimumEntryHeight * 1.5;

export type AllocationChartItemData = {
  evaluableId: string;
  allocation: number;
  evaluation?: FrontendEvaluation;
};

/**
 * Given a set of allocations and a screen size, determine how they should be
 * sorted and how vertical screen space shoudl be allocated among them.
 */
function getChartData({
  allocations,
  height,
}: {
  allocations: AllocationChartItemData[];
  height: number;
}): {
  topAllocations: AllocationChartItemProps[];
  otherAllocation: Pick<
    AllocationChartItemProps,
    "height" | "fontSize" | "allocation"
  >;
  smolAllocations: AllocationChartItemProps[];
} {
  // Split the allocations into top-allocations and smols. (The first
  // `numTopAllocations`, and everything else).
  //
  // TODO: Consider the case where there's a many-way tie crossing the
  // boundary, eg many allocations that are all exactly zero. In that case, we
  // might prefer fewer top allocations rather than an arbitrary tiebreak.
  const unilateralAllocationsSorted = allocations.sort(
    (a, b) => b.allocation - a.allocation,
  );
  const topAllocations = unilateralAllocationsSorted.slice(
    0,
    numTopAllocations,
  );
  const smolAllocations = unilateralAllocationsSorted.slice(numTopAllocations);
  const hasOtherBlock = smolAllocations.length > 0;

  // Summary stats
  const totalAllocatedToTop = sumBy(topAllocations, (a) => a.allocation);
  const totalAllocatedToSmols = sumBy(smolAllocations, (a) => a.allocation);
  const totalAllocated = totalAllocatedToTop + totalAllocatedToSmols;

  // Number of entries in the list, including the "Other" item that groups
  // the smols together but not including the smols themselves
  const numTopAllocationEntries =
    topAllocations.length + (hasOtherBlock ? 1 : 0);

  // Amount of excess height, which can be used to scale items to more than
  // their minimum size.
  const dynamicHeight = Math.max(
    0,
    height -
      numTopAllocationEntries * minimumEntryHeight -
      (hasOtherBlock ? pixOfSmolsToShowBelowOther : 0),
  );

  const yScale = (value: number) =>
    scaleLinear()
      .domain([0, totalAllocated])
      .range([minimumEntryHeight, dynamicHeight])(value);

  function getFontSize(value: number) {
    return heightToFontSize(yScale(value));
  }

  function heightToFontSize(height: number) {
    return height > 16 ? 13 : height - 4;
  }

  const topAllocationProps = topAllocations.map((allocation) => ({
    ...allocation,
    isSmol: false,
    height: yScale(allocation.allocation),
    fontSize: getFontSize(allocation.allocation),
  }));
  const smolAllocationProps = smolAllocations.map((allocation) => ({
    ...allocation,
    isSmol: true,
    height: minimumEntryHeight,
    fontSize: heightToFontSize(minimumEntryHeight),
  }));

  return {
    topAllocations: topAllocationProps,
    otherAllocation: {
      height: yScale(totalAllocatedToSmols),
      fontSize: getFontSize(totalAllocatedToSmols),
      allocation: totalAllocatedToSmols,
    },
    smolAllocations: smolAllocationProps,
  };
}

type AllocationChartProps = {
  allocations: AllocationChartItemData[];
  evaluator?: Recommender | Funder;
};

export const AllocationChart = withErrorBoundary(
  ({ allocations, evaluator }: AllocationChartProps) => {
    const [height, setHeight] = useState(400);
    const ref = useRef<HTMLDivElement>(null);
    useOnResize(() => {
      if (ref.current) {
        if (ref.current.clientHeight === height) {
          return;
        }
        setHeight(ref.current.clientHeight);
      }
    });

    const { topAllocations, otherAllocation, smolAllocations } = getChartData({
      allocations,
      height,
    });

    return (
      <div
        className="flex flex-col flex-1 overflow-x-hidden overflow-y-auto"
        ref={ref}
      >
        <div>
          {topAllocations.map((props) => (
            <AllocationChartItem
              key={props.evaluableId}
              {...props}
              evaluator={evaluator}
            />
          ))}
          {smolAllocations.length > 0 && (
            <AllocationChartItemOther key="Other" {...otherAllocation} />
          )}
          {smolAllocations.map((props) => (
            <AllocationChartItem
              key={props.evaluableId}
              {...props}
              evaluator={evaluator}
            />
          ))}
        </div>
      </div>
    );
  },
);
